import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, message, Modal,
  Row, Select, Space
} from 'antd';
import { CalculatorOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { investmentsApi } from './investmentsApi';
import {
  INCOME_MODES, findIncomeFormErrors, friendlyErrorFor, formatRupee
} from './investmentsHelpers';
import '../accounts.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investment Income modal (Stage 6B.9E).
//
// Operator picks date + mode + amount + (for income_received) the
// receiving cash/bank ledger → Preview → Post (Approval-MFA L1).
// ─────────────────────────────────────────────────────────────────

function LedgerSelect({ value, onChange, disabled, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const fetcher = useCallback(async (q) => {
    setLoading(true);
    try { return await investmentsApi.ledgerLookup(q || '', 100); }
    finally { setLoading(false); }
  }, []);
  const onOpen = useCallback(async (open) => {
    if (!open || options.length > 0) return;
    try { setOptions(await fetcher('')); } catch (_e) { /* */ }
  }, [options.length, fetcher]);
  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { setOptions(await fetcher(text || '')); } catch (_e) { /* */ }
    }, 200);
  }, [fetcher]);
  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder={placeholder || 'Search ledger…'}
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      allowClear
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: <span>
          <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
          {o.accountName}
        </span>
      }))}
    />
  );
}

export default function InvestmentIncomeModal({
  open, investment, onClose, onPosted, runWithApproval
}) {
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);
  const [mode, setMode] = useState('income_received');

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      txnDate: dayjs(),
      mode:    'income_received',
      amount:  0
    });
    setMode('income_received');
    setPreview(null);
  }, [open, form]);

  const runPreview = useCallback(async () => {
    if (!investment) return;
    try {
      const values = await form.validateFields();
      const body = {
        txnDate: values.txnDate.format('YYYY-MM-DD'),
        mode:    values.mode,
        amount:  Number(values.amount) || 0,
        receivingAccountId:    values.receivingAccountId || null,
        incomeLedgerAccountId: values.incomeLedgerAccountId || null,
        remarks: values.remarks || null
      };
      const errs = findIncomeFormErrors(body);
      if (errs.length) { message.error(errs.join(' ')); return; }
      setBusy(true);
      const out = await investmentsApi.previewIncome(investment.id, body);
      setPreview(out);
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [investment, form]);

  const runPost = useCallback(async () => {
    if (!investment) return;
    try {
      const values = await form.validateFields();
      const body = {
        txnDate: values.txnDate.format('YYYY-MM-DD'),
        mode:    values.mode,
        amount:  Number(values.amount) || 0,
        receivingAccountId:    values.receivingAccountId || null,
        incomeLedgerAccountId: values.incomeLedgerAccountId || null,
        remarks: values.remarks || null
      };
      const errs = findIncomeFormErrors(body);
      if (errs.length) { message.error(errs.join(' ')); return; }
      const confirmed = await new Promise((resolve) => {
        let okClicked = false;
        Modal.confirm({
          title: `Post investment income for ${investment.investmentCode}?`,
          icon:  <CheckCircleOutlined style={{ color: '#3f8600' }} />,
          content: `This will post an income JV through the engine for ₹ ${formatRupee(body.amount)}. You will be asked for a 6-digit Approval MFA code to confirm.`,
          okText: 'Post Income', cancelText: 'Cancel',
          onOk:    () => { okClicked = true; },
          onCancel: () => { okClicked = false; },
          afterClose: () => resolve(okClicked)
        });
      });
      if (!confirmed) return;
      setPosting(true);
      await runWithApproval({
        operationKey:   'accounts.investment.record_income',
        resourceType:   'investment',
        resourceId:     investment.id,
        operationLabel: 'Record investment income',
        resourceLabel:  `${investment.investmentCode} · ${investment.investmentName}`,
        action: (approvalMfaChallengeId) =>
          investmentsApi.postIncome(investment.id, body, { approvalMfaChallengeId })
      });
      message.success('Investment income posted.');
      onPosted && onPosted();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setPosting(false); }
  }, [investment, form, runWithApproval, onPosted]);

  if (!investment) return null;

  return (
    <Modal
      title={`Record Investment Income — ${investment.investmentCode} · ${investment.investmentName}`}
      open={open}
      onCancel={onClose}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      footer={
        <Space>
          <Button icon={<CalculatorOutlined />} onClick={runPreview} loading={busy}>
            Preview
          </Button>
          <Button type="primary" icon={<CheckCircleOutlined />} loading={posting} onClick={runPost}>
            Post Income
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form"
        onValuesChange={(_c, all) => { if ('mode' in (_c || {})) setMode(all.mode); }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Transaction Date" name="txnDate"
              rules={[{ required: true, message: 'Date is required.' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Income Mode" name="mode"
              rules={[{ required: true, message: 'Mode is required.' }]}>
              <Select options={INCOME_MODES.map((m) => ({ value: m.value, label: m.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Amount (₹)" name="amount"
              rules={[{ required: true, message: 'Amount is required.' }]}>
              <InputNumber min={0} step={100} style={{ width: '100%' }}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
        </Row>

        {mode === 'income_received' ? (
          <Form.Item label="Receiving Account (cash / bank / debtor)" name="receivingAccountId"
            rules={[{ required: true, message: 'Receiving account is required for received income.' }]}>
            <LedgerSelect placeholder="Cash / Bank / Sundry Debtor ledger" />
          </Form.Item>
        ) : null}

        <Form.Item label="Income Ledger (optional override)" name="incomeLedgerAccountId"
          tooltip="Defaults to the income ledger configured on the investment.">
          <LedgerSelect placeholder="Interest / dividend / income ledger" />
        </Form.Item>

        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000}
            placeholder="Optional — e.g. Q1 FY27 interest accrual" />
        </Form.Item>
      </Form>

      {!investment.incomeLedgerAccountId ? (
        <Alert type="warning" showIcon style={{ marginTop: 10 }}
          message="Income ledger not configured on this investment"
          description="Either set the income ledger on the investment itself (recommended for repeatability), or pick an override above before posting." />
      ) : null}

      {preview ? (
        <Alert type="info" showIcon style={{ marginTop: 10 }}
          message={`Preview — ${preview.mode} of ₹ ${formatRupee(preview.amount)} on ${String(preview.txnDate).slice(0, 10)}`}
          description={preview.message} />
      ) : (
        <Alert type="info" showIcon style={{ marginTop: 10 }}
          message="Pick a date, mode and amount, then press Preview to verify the entry before posting." />
      )}
    </Modal>
  );
}
